import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import ScanTicketZ, { champsLus } from "../components/ScanTicketZ";
import { getEvents, getFoodTruck, createPaymentCheckout, submitBilanPrestation, getBilanPrestation } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  CalendarCheck,
  MapPin,
  Clock,
  Users,
  Euro,
  CheckCircle,
  Calendar,
  AlertCircle,
  Loader2,
  CalendarX,
  CalendarDays,
  CreditCard,
  Gift,
  Info,
  Utensils,
  ExternalLink,
  Navigation,
  FileText,
  UtensilsCrossed,
  TrendingUp,
  Edit,
  PartyPopper,
} from "lucide-react";
import { Link } from "react-router-dom";
import { format, parseISO, isAfter, isBefore, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MesServicesPage() {
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [organisedServices, setOrganisedServices] = useState([]);
  const [foodTruck, setFoodTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingEventId, setPayingEventId] = useState(null);
  
  // Bilan state
  const [showBilanDialog, setShowBilanDialog] = useState(false);
  const [bilanEvent, setBilanEvent] = useState(null);
  const [bilanData, setBilanData] = useState({
    nombre_plats: "",
    ca_ht: "",
    ca_ttc: "",
    commentaire: "",
  });
  const [submittingBilan, setSubmittingBilan] = useState(false);
  const [existingBilan, setExistingBilan] = useState(null);

  useEffect(() => {
    if (user?.food_truck_id) {
      loadData();
    } else if (user && !user.food_truck_id) {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    if (!user?.food_truck_id) {
      setLoading(false);
      return;
    }
    
    try {
      const [eventsRes, ftRes, organisedRes] = await Promise.all([
        getEvents(),
        getFoodTruck(user.food_truck_id),
        axios.get(`${API}/api/organised-events/my-services/list`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      // Filtrer les événements où le food truck participe
      // participants can be either array of IDs (strings) or array of objects
      const myEvents = eventsRes.data.filter(event => {
        if (!event.participants) return false;
        return event.participants.some(p => {
          // Handle both formats: string ID or object with food_truck_id
          const ftId = typeof p === 'string' ? p : p.food_truck_id;
          return ftId === user.food_truck_id;
        });
      });
      
      setEvents(myEvents);
      setFoodTruck(ftRes.data);
      setOrganisedServices(organisedRes.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des événements");
    } finally {
      setLoading(false);
    }
  };

  const getParticipantInfo = (event) => {
    // First check event_participants collection data (enriched)
    if (event.participant_info) {
      return event.participant_info;
    }
    // Fallback: find in participants array
    const participant = event.participants?.find(p => {
      const ftId = typeof p === 'string' ? p : p.food_truck_id;
      return ftId === user.food_truck_id;
    });
    // If it's just an ID, return minimal info
    if (typeof participant === 'string') {
      return { food_truck_id: participant };
    }
    return participant;
  };

  const handlePayEvent = async (event) => {
    const participant = getParticipantInfo(event);
    if (!participant || participant.statut_paiement === "paye" || participant.statut_paiement === "exempte") {
      return;
    }

    setPayingEventId(event.id);
    try {
      const response = await createPaymentCheckout({
        package_id: "event_collectif",
        origin_url: window.location.origin,
        event_id: event.id,
        food_truck_id: user.food_truck_id
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
      setPayingEventId(null);
    }
  };

  const handlePayOrganisedService = async (service) => {
    if (service.statut_paiement === "paye" || service.statut_paiement === "exempte") {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/organised-events/service-payment/checkout?event_service_id=${service.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
    }
  };

  const openBilanDialog = async (event) => {
    setBilanEvent(event);
    setBilanData({ nombre_plats: "", ca_ht: "", ca_ttc: "", commentaire: "" });
    setExistingBilan(null);
    
    // Check if bilan already exists
    if (user?.food_truck_id) {
      try {
        const res = await getBilanPrestation(event.id, user.food_truck_id);
        if (res.data.rempli) {
          setExistingBilan(res.data);
          setBilanData({
            nombre_plats: res.data.nombre_plats?.toString() || "",
            ca_ht: res.data.ca_ht?.toString() || "",
            ca_ttc: res.data.ca_ttc?.toString() || "",
            commentaire: res.data.commentaire || "",
          });
        }
      } catch (error) {
        // Bilan doesn't exist yet, that's fine
      }
    }
    
    setShowBilanDialog(true);
  };

  const handleSubmitBilan = async () => {
    if (!bilanEvent || !user?.food_truck_id) return;
    
    if (!bilanData.nombre_plats || !bilanData.ca_ht || !bilanData.ca_ttc) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSubmittingBilan(true);
    try {
      await submitBilanPrestation(bilanEvent.id, user.food_truck_id, {
        nombre_plats: parseInt(bilanData.nombre_plats),
        ca_ht: parseFloat(String(bilanData.ca_ht).replace(/\s/g, "").replace(",", ".")),
        ca_ttc: parseFloat(String(bilanData.ca_ttc).replace(/\s/g, "").replace(",", ".")),
        commentaire: bilanData.commentaire,
      });
      toast.success("Bilan de prestation enregistré");
      setShowBilanDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmittingBilan(false);
    }
  };

  // Convert organised services to unified event format
  const convertOrganisedToEvents = () => {
    return organisedServices.map(service => {
      // Get the first date from event_dates for sorting
      const firstDate = service.event_dates?.[0]?.date || service.dates?.[0]?.date;
      const lastDate = service.event_dates?.[service.event_dates?.length - 1]?.date;
      
      return {
        id: `org_${service.id}`,
        titre: service.event_titre || service.titre,
        date_debut: firstDate,
        date_fin: lastDate || firstDate,
        lieu: service.event_lieu || service.lieu,
        adresse: service.event_adresse || service.adresse,
        type_evenement: "collectif",
        // Mark as organised event for UI differentiation if needed
        _isOrganisedEvent: true,
        _organisedService: service,
        // Payment info
        montant_du: service.montant_du,
        statut_paiement: service.statut_paiement,
        services_assignes: service.services_assignes,
        // Contact & additional info from enriched service
        contact_nom: service.contact_nom,
        contact_telephone: service.contact_telephone,
        information_complementaire: service.information_complementaire || service.event_description,
        couverts_estimes: service.couverts_estimes,
        has_bar: service.has_bar,
        bar_details: service.bar_details,
        client: service.client,
        event_dates: service.event_dates,
      };
    });
  };

  const categorizeEvents = () => {
    const now = new Date();
    const upcoming = [];
    const past = [];
    const today = [];

    // Combine regular events and organised events
    const organisedAsEvents = convertOrganisedToEvents();
    const allEvents = [...events, ...organisedAsEvents];

    allEvents.forEach(event => {
      const eventDate = parseISO(event.date_debut);
      if (isToday(eventDate)) {
        today.push(event);
      } else if (isAfter(eventDate, now)) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    // Sort by date
    const sortByDate = (a, b) => new Date(a.date_debut) - new Date(b.date_debut);
    upcoming.sort(sortByDate);
    today.sort(sortByDate);
    past.sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut)); // Most recent first

    return { upcoming, past, today };
  };

  const { upcoming, past, today } = categorizeEvents();

  const EventCard = ({ event }) => {
    const isOrganised = event._isOrganisedEvent;
    const participant = isOrganised ? null : getParticipantInfo(event);
    const eventDate = parseISO(event.date_debut);
    const isPast = isBefore(eventDate, new Date()) && !isToday(eventDate);
    const isCollectif = event.type_evenement === "collectif";
    const [showDetails, setShowDetails] = useState(false);
    
    // For organised events, get payment info from the service
    const organisedService = event._organisedService;
    
    return (
      <Card className={`bg-white border-[#E5E0DC] ${isPast ? "opacity-75" : ""}`}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            {/* Infos principales */}
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className={`p-3 rounded-lg ${isToday(eventDate) ? "bg-primary text-white" : "bg-primary/10 text-primary"}`}>
                  <Calendar className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-bold text-secondary">
                      {event.titre}
                    </h3>
                    {!isOrganised && (
                      <Link 
                        to={`/calendar?event=${event.id}`}
                        className="text-primary hover:text-primary-hover inline-flex items-center gap-1 text-sm"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Voir détails
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Badge variant={isCollectif ? "default" : "secondary"} className={isCollectif ? "bg-primary" : ""}>
                      {event.type_evenement === "collectif" ? "Collectif" : "Privé"}
                    </Badge>
                    {isToday(eventDate) && (
                      <Badge className="bg-status-success">Aujourd'hui</Badge>
                    )}
                    {event.nombre_food_trucks_requis && (
                      <Badge variant="outline" className="border-primary text-primary">
                        {event.nombre_food_trucks_requis} food trucks
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Détails essentiels */}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-secondary">
                  <CalendarDays className="w-4 h-4 text-primary" />
                  <span className="font-medium">
                    {/* Afficher la date de participation si différente de la date d'événement */}
                    {(() => {
                      const participantInfo = getParticipantInfo(event);
                      const dateParticipation = participantInfo?.date_participation;
                      if (dateParticipation && dateParticipation !== event.date_debut) {
                        return (
                          <>
                            {format(parseISO(dateParticipation), "EEEE d MMMM yyyy", { locale: fr })}
                            <span className="text-xs text-muted-foreground ml-1">(votre date)</span>
                          </>
                        );
                      }
                      return format(eventDate, "EEEE d MMMM yyyy", { locale: fr });
                    })()}
                  </span>
                </div>
                
                {event.heure_debut && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>
                      {event.heure_debut}
                      {event.heure_fin && ` - ${event.heure_fin}`}
                    </span>
                  </div>
                )}
                
                {event.lieu && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{event.lieu}</span>
                  </div>
                )}
                
                {event.adresse && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Navigation className="w-4 h-4" />
                    <span>{event.adresse}</span>
                  </div>
                )}

                {/* Contact info for organised events */}
                {isOrganised && event.contact_nom && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span>Contact : {event.contact_nom}</span>
                  </div>
                )}
                
                {isOrganised && event.contact_telephone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="text-lg">📞</span>
                    <a href={`tel:${event.contact_telephone}`} className="text-primary hover:underline">
                      {event.contact_telephone}
                    </a>
                  </div>
                )}
                
                {isOrganised && event.couverts_estimes && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <UtensilsCrossed className="w-4 h-4" />
                    <span>{event.couverts_estimes} couverts estimés</span>
                  </div>
                )}
                
                {isOrganised && event.client && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="text-lg">🏢</span>
                    <span>Client : {event.client}</span>
                  </div>
                )}

                {event.cree_par_nom && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span>Apporteur : {event.cree_par_nom}</span>
                  </div>
                )}
              </div>

              {/* Bouton voir plus de détails */}
              {(event.information_complementaire || event.menu || (isOrganised && (event.has_bar || event.event_dates?.length > 0))) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                  className="mt-3 text-primary hover:text-primary-hover p-0 h-auto"
                >
                  <Info className="w-4 h-4 mr-1" />
                  {showDetails ? "Masquer les détails" : "Voir tous les détails"}
                </Button>
              )}

              {/* Détails complets */}
              {showDetails && (
                <div className="mt-4 pt-4 border-t border-[#E5E0DC] space-y-3">
                  {/* Horaires des services pour événements organisés */}
                  {isOrganised && event.event_dates?.length > 0 && (
                    <div className="bg-[#F0F9FF] p-3 rounded-lg">
                      <p className="text-sm font-medium text-secondary mb-2 flex items-center gap-1">
                        <Clock className="w-4 h-4 text-primary" /> Horaires des services
                      </p>
                      <div className="space-y-2">
                        {event.event_dates.map((dateInfo, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium">{format(parseISO(dateInfo.date), "EEEE d MMMM", { locale: fr })} :</span>
                            <div className="ml-4 space-y-1">
                              {dateInfo.services?.map((service, sIdx) => (
                                <div key={sIdx} className="text-muted-foreground">
                                  {service.type === "midi" ? "🌞 Midi" : "🌙 Soir"} : {service.horaire_debut} - {service.horaire_fin}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {isOrganised && event.has_bar && (
                    <div className="bg-[#FFF0F5] p-3 rounded-lg">
                      <p className="text-sm font-medium text-secondary mb-1 flex items-center gap-1">
                        🍹 Service Bar inclus
                      </p>
                      {event.bar_details && (
                        <p className="text-sm text-muted-foreground">{event.bar_details}</p>
                      )}
                    </div>
                  )}
                  
                  {event.information_complementaire && (
                    <div className="bg-[#F9F7F5] p-3 rounded-lg">
                      <p className="text-sm font-medium text-secondary mb-1 flex items-center gap-1">
                        <Info className="w-4 h-4 text-primary" /> Informations complémentaires
                      </p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {event.information_complementaire}
                      </p>
                    </div>
                  )}
                  
                  {event.menu && (
                    <div className="bg-[#FFF8F0] p-3 rounded-lg">
                      <p className="text-sm font-medium text-secondary mb-1 flex items-center gap-1">
                        <Utensils className="w-4 h-4 text-primary" /> Menu / Offre attendue
                      </p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {event.menu}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Services assignés pour événements organisés */}
              {isOrganised && event.services_assignes?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-secondary mb-2">Vos services:</p>
                  <div className="flex flex-wrap gap-2">
                    {event.services_assignes.map(slot => {
                      const parts = slot.split("_");
                      const dateStr = parts[0];
                      const type = parts[1];
                      return (
                        <Badge key={slot} variant="outline" className="text-xs border-primary text-primary">
                          {format(parseISO(dateStr), "d MMM", { locale: fr })} - {type === "midi" ? "Midi" : "Soir"}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Statut paiement - Regular events */}
            {isCollectif && participant && !isOrganised && (
              <div className="flex flex-col items-end gap-2">
                {participant.statut_paiement === "exempte" ? (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Membre du bureau</p>
                      <p className="text-lg font-bold text-status-success">Exempté</p>
                    </div>
                    <Badge className="bg-accent">
                      <Gift className="w-3 h-3 mr-1" /> Exempté
                    </Badge>
                  </>
                ) : participant.statut_paiement === "paye" ? (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Participation</p>
                      <p className="text-2xl font-bold text-primary">{participant.montant_du || 10} €</p>
                    </div>
                    <Badge className="bg-status-success">
                      <CheckCircle className="w-3 h-3 mr-1" /> Payé
                    </Badge>
                  </>
                ) : (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Participation</p>
                      <p className="text-2xl font-bold text-primary">{participant.montant_du || 10} €</p>
                    </div>
                    <Badge variant="outline" className="border-status-warning text-status-warning">
                      <AlertCircle className="w-3 h-3 mr-1" /> À payer
                    </Badge>
                    {!isPast && (
                      <Button
                        size="sm"
                        onClick={() => handlePayEvent(event)}
                        disabled={payingEventId === event.id}
                        className="bg-primary hover:bg-primary-hover mt-2"
                        data-testid={`pay-event-${event.id}`}
                      >
                        {payingEventId === event.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Payer en ligne
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Statut paiement - Organised events */}
            {isOrganised && organisedService && (
              <div className="flex flex-col items-end gap-2">
                {organisedService.statut_paiement === "exempte" ? (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Membre du bureau</p>
                      <p className="text-lg font-bold text-status-success">Exempté</p>
                    </div>
                    <Badge className="bg-accent">
                      <Gift className="w-3 h-3 mr-1" /> Exempté
                    </Badge>
                  </>
                ) : organisedService.statut_paiement === "paye" ? (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Participation</p>
                      <p className="text-2xl font-bold text-primary">{organisedService.montant_du?.toFixed(2)} €</p>
                    </div>
                    <Badge className="bg-status-success">
                      <CheckCircle className="w-3 h-3 mr-1" /> Payé
                    </Badge>
                  </>
                ) : (
                  <>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Participation</p>
                      <p className="text-2xl font-bold text-primary">{organisedService.montant_du?.toFixed(2)} €</p>
                    </div>
                    <Badge variant="outline" className="border-status-warning text-status-warning">
                      <AlertCircle className="w-3 h-3 mr-1" /> À payer
                    </Badge>
                    {!isPast && (
                      <Button
                        size="sm"
                        onClick={() => handlePayOrganisedService(organisedService)}
                        className="bg-primary hover:bg-primary-hover mt-2"
                        data-testid={`pay-organised-${organisedService.id}`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Payer en ligne
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Section Bilan pour événements passés (seulement pour événements non-organisés) */}
          {isPast && participant && !isOrganised && (
            <div className="mt-4 pt-4 border-t border-[#E5E0DC]">
              {participant.bilan_rempli ? (
                <div className="bg-[#F0FFF4] p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-secondary flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-status-success" />
                      Bilan de prestation
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openBilanDialog(event)}
                      className="text-primary hover:text-primary-hover"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xs text-muted-foreground">Plats vendus</p>
                      <p className="text-lg font-bold text-secondary">{participant.bilan_nombre_plats}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xs text-muted-foreground">CA HT</p>
                      <p className="text-lg font-bold text-primary">{participant.bilan_ca_ht?.toFixed(2)} €</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xs text-muted-foreground">CA TTC</p>
                      <p className="text-lg font-bold text-accent">{participant.bilan_ca_ttc?.toFixed(2)} €</p>
                    </div>
                  </div>
                  {participant.bilan_commentaire && (
                    <p className="mt-3 text-sm text-muted-foreground italic">
                      "{participant.bilan_commentaire}"
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-secondary flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-600" />
                        Bilan à remplir
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Partagez vos résultats de cette prestation
                      </p>
                    </div>
                    <Button
                      onClick={() => openBilanDialog(event)}
                      className="bg-primary hover:bg-primary-hover"
                      data-testid={`fill-bilan-${event.id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Remplir le bilan
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="mes-services-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
          Mes Services
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          Événements de {foodTruck?.nom_food_truck || "votre food truck"}
        </p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-status-success/10 rounded-lg">
              <CalendarCheck className="w-4 h-4 sm:w-6 sm:h-6 text-status-success" />
            </div>
            <div>
              <p className="text-lg sm:text-2xl font-bold text-secondary">{today.length}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">Auj.</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-primary/10 rounded-lg">
              <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <p className="text-lg sm:text-2xl font-bold text-secondary">{upcoming.length}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">À venir</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-[#F0EBE6] rounded-lg">
              <CalendarX className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-lg sm:text-2xl font-bold text-secondary">{past.length}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">Passés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des événements */}
      <Tabs defaultValue="upcoming" className="space-y-4 sm:space-y-6">
        <TabsList className="bg-[#F0EBE6] w-full sm:w-auto">
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none">
            À venir ({upcoming.length + today.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none">
            Historique ({past.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {today.length === 0 && upcoming.length === 0 ? (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-secondary">Aucun événement à venir</h3>
                <p className="text-muted-foreground mt-1">
                  Vous serez notifié lorsque vous serez inscrit à un nouvel événement
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {today.length > 0 && (
                <div className="space-y-3">
                  <h2 className="font-heading text-lg font-bold text-secondary flex items-center gap-2">
                    <CalendarCheck className="w-5 h-5 text-status-success" />
                    Aujourd'hui
                  </h2>
                  {today.map(event => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              )}
              
              {upcoming.length > 0 && (
                <div className="space-y-3">
                  <h2 className="font-heading text-lg font-bold text-secondary flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Prochains événements
                  </h2>
                  {upcoming.map(event => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past">
          {past.length === 0 ? (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-8 text-center">
                <CalendarX className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-secondary">Aucun événement passé</h3>
                <p className="text-muted-foreground mt-1">
                  L'historique de vos participations apparaîtra ici
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {past
                .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut))
                .map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Bilan de Prestation */}
      <Dialog open={showBilanDialog} onOpenChange={setShowBilanDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Bilan de prestation
            </DialogTitle>
            <DialogDescription>
              {bilanEvent?.titre} - {bilanEvent?.date_debut && format(parseISO(bilanEvent.date_debut), "d MMMM yyyy", { locale: fr })}
            </DialogDescription>
          </DialogHeader>
          
          {existingBilan && (
            <div className="p-3 bg-status-success/10 rounded-lg border border-status-success/30 mb-4">
              <p className="text-sm text-status-success flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Bilan déjà rempli
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <ScanTicketZ onLu={(r) => setBilanData((b) => ({ ...b, ...champsLus(r) }))} />
            <div>
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-primary" />
                Nombre de plats vendus *
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                value={bilanData.nombre_plats}
                onChange={(e) => setBilanData({ ...bilanData, nombre_plats: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Ex: 150"
                data-testid="bilan-nombre-plats"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4 text-primary" />
                  CA HT (€) *
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={bilanData.ca_ht}
                  onChange={(e) => setBilanData({ ...bilanData, ca_ht: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Ex: 1200.00"
                  data-testid="bilan-ca-ht"
                />
              </div>
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4 text-accent" />
                  CA TTC (€) *
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={bilanData.ca_ttc}
                  onChange={(e) => setBilanData({ ...bilanData, ca_ttc: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Ex: 1440.00"
                  data-testid="bilan-ca-ttc"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Commentaire (optionnel)</Label>
              <Textarea
                value={bilanData.commentaire}
                onChange={(e) => setBilanData({ ...bilanData, commentaire: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Remarques sur la prestation..."
                rows={3}
                data-testid="bilan-commentaire"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBilanDialog(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitBilan}
              disabled={submittingBilan}
              className="bg-primary hover:bg-primary-hover"
              data-testid="submit-bilan-btn"
            >
              {submittingBilan ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {existingBilan ? "Modifier" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
